import React, { createContext, useContext, useState, useEffect } from "react";
import { ConsentState } from "../types/Cookie";
import { CONSENT_KEY } from "../constants/cookie-constants";

interface CookieConsentContextType {
    consentState: ConsentState;
    hasConsented: boolean;
    acceptConsent: () => void;
    declineConsent: () => void;
    resetConsent: () => void;
    showBanner: boolean;
}

const CookieConsentContext = createContext<
    CookieConsentContextType | undefined
>(undefined);

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [consentState, setConsentState] = useState<ConsentState>(
        ConsentState.PENDING
    );

    useEffect(() => {
        const savedConsent = localStorage.getItem(CONSENT_KEY);
        if (
            savedConsent === ConsentState.ACCEPTED ||
            savedConsent === ConsentState.DECLINED
        ) {
            setConsentState(savedConsent as ConsentState);
        }
    }, []);

    const acceptConsent = () => {
        localStorage.setItem(CONSENT_KEY, ConsentState.ACCEPTED);
        setConsentState(ConsentState.ACCEPTED);
    };

    const declineConsent = () => {
        localStorage.setItem(CONSENT_KEY, ConsentState.DECLINED);
        setConsentState(ConsentState.DECLINED);
    };

    const resetConsent = () => {
        localStorage.removeItem(CONSENT_KEY);
        setConsentState(ConsentState.PENDING);
    };

    return (
        <CookieConsentContext.Provider
            value={{
                consentState,
                hasConsented: consentState === ConsentState.ACCEPTED,
                acceptConsent,
                declineConsent,
                resetConsent,
                showBanner: consentState === ConsentState.PENDING,
            }}
        >
            {children}
        </CookieConsentContext.Provider>
    );
};

export const useCookieConsent = () => {
    const context = useContext(CookieConsentContext);

    if (context === undefined) {
        throw new Error(
            "useCookieConsent must be used within a CookieConsentProvider"
        );
    }

    return context;
};
